import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import "./CompanyDetails.styles.scss";
import "./CompanyDetails.detailed.css";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import UnitTrust from "./unitTrust.Component";
import ShareCapitalComponent from "./ShareCapitalComponent";
import DocumentHistoryComponent from "../CompanySnapshotComponent/DocumentHistoryComponent";
import Popup from "../CommonPage/Popup.component";
import PrintCompanyProfileComponent from "../PrintCompanyProfileComponents/PrintCompanyProfile.Component";
import ToggleSwitch from "../CommonPage/ToggleSwitch.component";
import ThreeWayToggle from "../CommonPage/ThreeWayToggle.component";
import { userActions } from "../../redux/actions/user.actions";
import OrgStructureChart from "../../components/OrgStructureChart/OrgStructureChart";
import FullPageLoader from "../CommonPage/FullPageLoader";
import ShareHolderDetailsComponent from "../ShareHolderDetailsComponent/ShareHolderDetails.component";
import { useHistory } from "react-router-dom";
import OfficerDetailsComponent from "../OfficerDetailsComponents/OfficerDetails.Component";
import CompanySnapshotComponent from "../CompanySnapshotComponent/CompanySnapshotComponent.component";
import EmailId from "../EmailIdComponent/EmailId.Component";
import DidComponent from "../Did.Component/Did.Component";
import SignedDocumentsComponent from "../DocumentsComponent/SignedDocumentsComponent";
import OtherDocumentsComponent from "../DocumentsComponent/OtherDocumentsComponent";
import DocumentTimeline from "../../components/DocsTimeline/DocumentTimeline";
import CapTable from "../../components/CapTable/CapTable";

import DetailedViewIcon from '../../components/icons/DetailedViewIcon.tsx';
import SnapshotViewIcon from '../../components/icons/SnapshotViewIcon.tsx';
import DocsTimelineIcon from '../../components/icons/DocsTimelineIcon.tsx';
import CapTableIcon from '../../components/icons/CapTableIcon.tsx';
import OrgChartIcon from '../../components/icons/OrgChartIcon.tsx';

const VIEW_OPTIONS = [
  {
    label: "Detailed View",
    icon: DetailedViewIcon,
  },
  {
    label: "Snapshot View",
    icon: SnapshotViewIcon,
  },
  {
    label: "Docs Timeline",
    icon: DocsTimelineIcon,
  },
  {
    label: "Cap Table",
    icon: CapTableIcon,
  },
  {
    label: "Org Chart",
    icon: OrgChartIcon,
  },
];

/* ─────────────────────────────────────
 *  DATA TRANSFORM LAYER
 *  Maps raw API JSON → clean UI models
 * ───────────────────────────────────── */

function fmtDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function buildAddress(prefix, obj) {
  if (!obj) return null;
  const parts = [
    obj[`${prefix}_CARE_OF`],
    obj[`${prefix}_BUILDING`],
    obj[`${prefix}_STREET`],
  ].filter(Boolean).filter(s => s.trim());
  const suburb = obj[`${prefix}_SUBURB`];
  const state = obj[`${prefix}_STATE`];
  const pcode = obj[`${prefix}_PCODE`];
  const country = obj[`${prefix}_COUNTRY`];
  const line2 = [suburb, state, pcode && pcode !== 0 ? String(Math.round(pcode)) : null].filter(Boolean).join(' ');
  const countryStr = country && country.trim() && country.trim().toUpperCase() !== 'AUSTRALIA' ? country.trim() : null;
  return [...parts, line2, countryStr].filter(Boolean).join(', ') || null;
}

function mapCompanyData(items) {
  if (!items || !items.company) return null;
  const c = items.company;
  return {
    name: c.CO_NAME,
    code: c.CODE,
    acn: c.ACN,
    abn: c.ABN,
    dateInc: fmtDate(c.DATE_INC),
    placeInc: c.PLACE_INC,
    reviewDate: fmtDate(c.REVIEW_DATE),
    asicDebt: c.DEBT_AMOUNT,
    asicDebtDate: c.DEBT_DATE,
    agentCode: c.AGENT_CODE,
    regOffice: buildAddress('RO', c),
    principalPlace: buildAddress('PB', c),
    postalAddress: [c.POSTAL_CAREOF, c.POSTAL1, c.POSTAL2, c.POSTAL3,
      c.POST_STATE, c.POST_PCODE && c.POST_PCODE !== 0 ? String(Math.round(c.POST_PCODE)) : null
    ].filter(Boolean).join(', ') || null,
    email: c.EMAIL_ADDRESS,
    occupier: c.OCCUP_CO_NAME,
    occupierConsent: c.OCCUP_CONS,
    inactive: c.INACTIVE,
    deregistered: c.DEREGISTERED,
    formerName: c.FORMER_NAME !== c.CO_NAME ? c.FORMER_NAME : null,
    asicClass: c.ASC_CLASS_DESC,
    asicSubClass: c.ASC_CO_TYP_DESC,
    asicType: c.ASC_S_CLAS_DESC,
    activity: c.ACTIVITY,
    activity2: c.ACTIVITY2,
    clientAccount: c.CLIENT_ACCOUNT_CODE,
    clientBilling: c.CLIENT_BILLING_CODE,
    registeredAgent: c.CA_AGENT_NAME,
    registeredAgentNo: c.CA_AGENT_NO,
  };
}

function mapOfficersData(items) {
  if (!items || !items.officers) return [];
  return items.officers.map(o => ({
    entityCode: o.CODE,
    name: o.OFFICER_NAME,
    position: o.POSITION_HELD,
    appointed: fmtDate(o.APPOINTED),
    ceased: fmtDate(o.CEASED),
    reason: o.REASON,
    defaultSig: o.DEF_SIGN,
    email: o.EMAIL || null,
    did: o.DID,
    dob: fmtDate(o.DOB),
    address: [o.ADD_STREET, o.ADD_SUBURB, o.ADD_STATE, o.ADD_PCODE].filter(Boolean).join(', '),
    isCurrent: !o.CEASED,
  }));
}

function mapSharecapsData(items) {
  if (!items || !items.sharecaps) return [];
  return items.sharecaps.map(s => ({
    code: s.CLASS_CODE,
    desc: s.CLASS_DESC,
    issued: s.ISSUED || 0,
    nomValue: s.NOM_VALUE || 0,
    paidUp: s.PAID_UP || 0,
    duePayable: s.DUE_AND_PAYABLE || 0,
  }));
}

function mapShareholdersData(items) {
  if (!items || !items.shareholders) return [];
  return items.shareholders.map(s => ({
    entityCode: s.CODE,
    name: s.NAME,
    classCode: s.CLASS_CODE,
    shares: s.SUM_TOT_SHARES || 0,
    fullyPaid: s.FULLY_PAID,
    benOwner: s.BEN_OWNER,
    amountPaid: s.AMOUNT_PAID,
    amountUnpaid: s.AMOUNT_UNPAID,
    email: s.EMAIL_ADDRESS,
    address: [s.ADD_STREET, s.ADD_SUBURB, s.ADD_STATE, s.ADD_PCODE].filter(Boolean).join(', '),
    changeDate: fmtDate(s.CHANGE_DATE),
  }));
}

function mapDocumentsData(items) {
  if (!items || !items.documentHistory) return [];
  return items.documentHistory.map(d => ({
    docNo: d.DOC_NO,
    formType: d.FORM_TYPE,
    formDesc: d.FORM_TYPE_DESC,
    formDate: fmtDate(d.FORM_DATE),
    lodgeDate: fmtDate(d.LODGE_DATE),
    els: d.ELS_TRANSMIT ? 'Y' : 'N',
    asicDocNo: d.ASIC_DOC_NO || null,
    status: d.STATUS || null,
    statusDetail: d.STATUS_DETAIL || null,
    year: d.FORM_DATE ? new Date(d.FORM_DATE).getFullYear()
      : (d.LODGE_DATE ? new Date(d.LODGE_DATE).getFullYear() : null),
  }));
}


/* ─────────────────────────────────────
 *  ICONS (SVG components)
 * ───────────────────────────────────── */

const Icons = {
  copy: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  ),
  check: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  user: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  doc: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  ),
  chevron: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  ),
  briefcase: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="7" width="20" height="14" rx="2"/>
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
    </svg>
  ),
  shield: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="16"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
  userSingle: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
    </svg>
  ),
  edit: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  close: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  extLink: (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
      <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
    </svg>
  ),
  building: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/>
      <line x1="8" y1="6" x2="8" y2="6.01"/><line x1="12" y1="6" x2="12" y2="6.01"/><line x1="16" y1="6" x2="16" y2="6.01"/>
      <line x1="8" y1="10" x2="8" y2="10.01"/><line x1="12" y1="10" x2="12" y2="10.01"/><line x1="16" y1="10" x2="16" y2="10.01"/>
      <line x1="8" y1="14" x2="8" y2="14.01"/><line x1="12" y1="14" x2="12" y2="14.01"/><line x1="16" y1="14" x2="16" y2="14.01"/>
    </svg>
  ),
};


/* ─────────────────────────────────────
 *  PRIMITIVES
 * ───────────────────────────────────── */

function Badge({ children, variant = 'default' }) {
  return <span className={`pc-badge pc-badge--${variant}`}>{children}</span>;
}

function FormBadge({ formType }) {
  const map = {
    '480': 'form-480', '484': 'form-484', '362': 'form-362',
    'Dividend': 'form-div', 'RA71': 'form-ra71', 'RC09': 'form-rc09',
  };
  return <Badge variant={map[formType] || 'default'}>{formType}</Badge>;
}

function CopyButton({ value }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    if (navigator.clipboard && value) {
      navigator.clipboard.writeText(value);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      className={`pc-copy-btn ${copied ? 'pc-copy-btn--copied' : ''}`}
      onClick={handleCopy}
      title="Copy to clipboard"
    >
      {copied ? Icons.check : Icons.copy}
    </button>
  );
}

function DataRow({ label, value, mono, copyable }) {
  const display = value && value !== 'n/a' && String(value).trim() ? String(value) : null;
  return (
    <div className="pc-row">
      <span className="pc-row__label">{label}</span>
      <div className="pc-row__value-wrap">
        <span className={`pc-row__value ${mono ? 'pc-row__value--mono' : ''} ${!display ? 'pc-row__value--empty' : ''}`}>
          {display || 'Not set'}
        </span>
        {copyable && display && <CopyButton value={display} />}
      </div>
    </div>
  );
}

function Collapse({ title, icon, count, children, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="pc-collapse">
      <button className="pc-collapse__header" onClick={() => setOpen(!open)}>
        <div className="pc-collapse__header-left">
          <span className="pc-collapse__icon">{icon}</span>
          <span className="pc-collapse__title">{title}</span>
          {count != null && <span className="pc-collapse__count">{count}</span>}
        </div>
        <span className={`pc-collapse__chevron ${open ? 'pc-collapse__chevron--open' : ''}`}>
          {Icons.chevron}
        </span>
      </button>
      {open && <div className="pc-collapse__body">{children}</div>}
    </div>
  );
}


/* ─────────────────────────────────────
 *  ENTITY LINK + ASSOCIATED COMPANIES POPUP
 * ───────────────────────────────────── */

function EntityLink({ name, entityCode, entityLookup, onCompanyChange, currentCompanyCode, className }) {
  const [open, setOpen] = useState(false);
  const [companies, setCompanies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const trigRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) && trigRef.current && !trigRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handleClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (!companies && !loading) {
      setLoading(true);
      setError(null);
      try {
        if (entityLookup) {
          const res = await entityLookup(entityCode);
          setCompanies(res || []);
        } else {
          setCompanies([{ code: currentCompanyCode, name: 'Current Company', acn: '—', role: 'Current' }]);
        }
      } catch (err) {
        setError('Failed to load companies');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  }, [open, companies, loading, entityCode, entityLookup, currentCompanyCode]);

  const handleGo = useCallback((code) => {
    setOpen(false);
    if (code !== currentCompanyCode && onCompanyChange) onCompanyChange(code);
  }, [currentCompanyCode, onCompanyChange]);

  return (
    <span className="pc-entity-link-wrapper">
      <a ref={trigRef} href="#" className={`pc-entity-link ${className || ''} ${open ? 'pc-entity-link--active' : ''}`} onClick={handleClick} title={`View companies for ${name}`}>
        {name}
      </a>
      {open && (
        <div ref={popRef} className="pc-entity-popup">
          <div className="pc-entity-popup__header">
            <div className="pc-entity-popup__header-icon">{Icons.user}</div>
            <div className="pc-entity-popup__header-info">
              <div className="pc-entity-popup__header-name">{name}</div>
              <div className="pc-entity-popup__header-sub">Associated Companies</div>
            </div>
            <button className="pc-entity-popup__close" onClick={() => setOpen(false)}>{Icons.close}</button>
          </div>
          <div className="pc-entity-popup__body">
            {loading && (
              <div className="pc-entity-popup__loading">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="pc-spinner">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
                <span>Loading…</span>
              </div>
            )}
            {error && (
              <div className="pc-entity-popup__error">
                <span>{error}</span>
                <button className="pc-entity-popup__retry" onClick={() => { setCompanies(null); setError(null); }}>Retry</button>
              </div>
            )}
            {!loading && !error && companies && companies.length === 0 && (
              <div className="pc-entity-popup__empty">No associated companies found</div>
            )}
            {!loading && !error && companies && companies.length > 0 && (
              <ul className="pc-entity-popup__list">
                {companies.map((c, i) => {
                  const isCurrent = c.code === currentCompanyCode;
                  return (
                    <li key={i} className="pc-entity-popup__item">
                      <a href="#" className={`pc-entity-popup__company ${isCurrent ? 'pc-entity-popup__company--current' : ''}`} onClick={(e) => { e.preventDefault(); handleGo(c.code); }}>
                        <div className="pc-entity-popup__company-icon">{Icons.building}</div>
                        <div className="pc-entity-popup__company-info">
                          <div className="pc-entity-popup__company-name">
                            {c.name}
                            {isCurrent && <span className="pc-entity-popup__current-badge">Current</span>}
                          </div>
                          <div className="pc-entity-popup__company-meta">
                            <span>ACN {c.acn}</span>
                            {c.role && (
                              <>
                                <span className="pc-entity-popup__meta-sep">·</span>
                                <span>{c.role}</span>
                              </>
                            )}
                          </div>
                        </div>
                        {!isCurrent && <span className="pc-entity-popup__go-icon">{Icons.extLink}</span>}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </span>
  );
}


/* ─────────────────────────────────────
 *  DETAILED VIEW SECTION COMPONENT
 * ───────────────────────────────────── */

function DetailedViewSection({ companyProfile, entityLookup, onCompanyChange }) {
  const [showCurrent, setShowCurrent] = React.useState(true);
  const [docFilter, setDocFilter] = React.useState('all');

  // Transform data from Redux state
  const co = useMemo(() => mapCompanyData(companyProfile?.items), [companyProfile]);
  const officers = useMemo(() => mapOfficersData(companyProfile?.items), [companyProfile]);
  const shares = useMemo(() => mapSharecapsData(companyProfile?.items), [companyProfile]);
  const shareholders = useMemo(() => mapShareholdersData(companyProfile?.items), [companyProfile]);
  const docs = useMemo(() => mapDocumentsData(companyProfile?.items), [companyProfile]);

  // Loading/Error states
  if (companyProfile?.loading) {
    return (
      <div className="pc-shell">
        <div className="pc-content" style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!co) {
    return (
      <div className="pc-shell">
        <div className="pc-content" style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
          No company data available
        </div>
      </div>
    );
  }

  const currentOfficers = officers.filter(o => o.isCurrent);
  const ceasedOfficers = officers.filter(o => !o.isCurrent);
  const displayOfficers = showCurrent ? currentOfficers : ceasedOfficers;

  const formTypes = ['all', ...Array.from(new Set(docs.map(d => d.formType))).sort()];
  const filteredDocs = docFilter === 'all' ? docs : docs.filter(d => d.formType === docFilter);

  return (
    <div className="pc-shell">
      {/* Top Bar */}
      <div className="pc-topbar">
        <div className="pc-topbar__left">
          <span className="pc-topbar__company-name">{co.name}</span>
          {co.inactive && <Badge variant="inactive">Inactive</Badge>}
          {co.deregistered && <Badge variant="ceased">Deregistered</Badge>}
          {!co.inactive && !co.deregistered && <Badge variant="active">Active</Badge>}
        </div>
      </div>

      {/* Content */}
      <div className="pc-content">
        <div className="pc-content--animated">
          {/* Company Details */}
          <Collapse title="Company Details" icon={Icons.briefcase}>
            <DataRow label="Company Name" value={co.name} />
            <DataRow label="ACN" value={co.acn} mono copyable />
            <DataRow label="ABN" value={co.abn} mono copyable />
            <DataRow label="Date & Place of Incorporation" value={co.dateInc ? `${co.dateInc} — ${co.placeInc || ''}` : null} />
            <DataRow label="Review Date" value={co.reviewDate} />
            <DataRow label={`ASIC Debt (as at ${co.asicDebtDate || 'n/a'})`} value={co.asicDebt || '$0.00'} />
            <DataRow label="Registered Office" value={co.regOffice} />
            <DataRow label="Occupier" value={co.occupier} />
            <DataRow label="Principal Place of Business" value={co.principalPlace} />
            <DataRow label="Postal Address" value={co.postalAddress} />
            <DataRow label="Email Address" value={co.email} />
            {co.formerName && <DataRow label="Former Name" value={co.formerName} />}
            <DataRow label="Registered Agent" value={co.registeredAgent ? `${co.registeredAgent} (${co.registeredAgentNo})` : null} />
          </Collapse>

          {/* Entity Details */}
          <Collapse title="Entity Details" icon={Icons.shield}>
            <DataRow label="ASIC Company Class" value={co.asicClass} />
            <DataRow label="ASIC Company Sub-Class" value={co.asicSubClass} />
            <DataRow label="ASIC Company Type" value={co.asicType} />
            <DataRow label="Principal Activity" value={co.activity} />
            <DataRow label="Principal Activity Line 2" value={co.activity2} />
            <DataRow label="Client Account Code" value={co.clientAccount} />
            <DataRow label="Client Billing Code" value={co.clientBilling} />
          </Collapse>

          {/* Officers */}
          <Collapse title="Officers" count={officers.length} icon={Icons.users}>
            <div className="pc-filters">
              <button
                className={`pc-filter-btn ${showCurrent ? 'pc-filter-btn--active' : ''}`}
                onClick={() => setShowCurrent(true)}
              >
                Current ({currentOfficers.length})
              </button>
              <button
                className={`pc-filter-btn ${!showCurrent ? 'pc-filter-btn--active-red' : ''}`}
                onClick={() => setShowCurrent(false)}
              >
                Ceased ({ceasedOfficers.length})
              </button>
            </div>
            {displayOfficers.length > 0 ? displayOfficers.map((o, i) => (
              <div className="pc-officer-card" key={i}>
                <div className={`pc-officer-card__avatar ${o.isCurrent ? 'pc-officer-card__avatar--current' : 'pc-officer-card__avatar--ceased'}`}>
                  {Icons.user}
                </div>
                <div className="pc-officer-card__info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '2px', flexWrap: 'wrap' }}>
                    <EntityLink
                      name={o.name}
                      entityCode={o.entityCode}
                      entityLookup={entityLookup}
                      onCompanyChange={onCompanyChange}
                      currentCompanyCode={co.code}
                      className="pc-officer-card__name-link"
                    />
                    <Badge variant={o.position === 'DIRECTOR' ? 'director' : 'secretary'}>{o.position}</Badge>
                    {o.defaultSig && <Badge variant="sig">Default Sig</Badge>}
                    {!o.isCurrent && <Badge variant="ceased">Ceased {o.ceased}</Badge>}
                  </div>
                  <div className="pc-officer-card__meta">
                    <span>Appointed {o.appointed}</span>
                    {o.email && (
                      <>
                        <span className="pc-officer-card__meta-sep">·</span>
                        <span className="pc-officer-card__email">{o.email}</span>
                      </>
                    )}
                    {!o.isCurrent && o.reason && (
                      <>
                        <span className="pc-officer-card__meta-sep">·</span>
                        <span>{o.reason}</span>
                      </>
                    )}
                  </div>
                </div>
                <button className="pc-officer-card__did-btn">{Icons.edit} DID</button>
              </div>
            )) : (
              <div style={{ padding: '12px 0', color: '#94A3B8', fontSize: '13px' }}>
                No {showCurrent ? 'current' : 'ceased'} officers found
              </div>
            )}
          </Collapse>

          {/* Share Capital */}
          <Collapse title="Share Capital" count={`${shares.length} class${shares.length !== 1 ? 'es' : ''}`} icon={Icons.plus}>
            {shares.length > 0 ? (
              <>
                <div className="pc-grid--shares pc-table-header">
                  <span>Class</span><span>Issued</span><span>Nominal Value</span><span>Paid Up</span><span>Due & Payable</span>
                </div>
                {shares.map((s, i) => (
                  <div className="pc-grid--shares pc-table-row" key={i}>
                    <span><Badge variant={s.code}>{s.code} — {s.desc}</Badge></span>
                    <span className="pc-table-cell--mono">{s.issued.toLocaleString()}</span>
                    <span className="pc-table-cell--mono">${(s.nomValue || 0).toFixed(2)}</span>
                    <span className="pc-table-cell--mono">${(s.paidUp || 0).toLocaleString()}</span>
                    <span className="pc-table-cell--mono">${(s.duePayable || 0).toLocaleString()}</span>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ padding: '12px 0', color: '#94A3B8', fontSize: '13px' }}>
                No share capital data available
              </div>
            )}
          </Collapse>

          {/* Shareholders */}
          <Collapse title="Shareholders" count={shareholders.length} icon={Icons.userSingle}>
            {shareholders.length > 0 ? (
              <>
                <div className="pc-grid--shareholders pc-table-header">
                  <span>Name</span><span>Class</span><span>Shares</span><span>Ben. Owner</span><span>Fully Paid</span><span>Email</span>
                </div>
                {shareholders.map((s, i) => (
                  <div className="pc-grid--shareholders pc-table-row" key={i}>
                    <div>
                      <EntityLink
                        name={s.name}
                        entityCode={s.entityCode}
                        entityLookup={entityLookup}
                        onCompanyChange={onCompanyChange}
                        currentCompanyCode={co.code}
                      />
                      {s.address && <div className="pc-table-cell--sub">{s.address}</div>}
                    </div>
                    <span><Badge variant={s.classCode}>{s.classCode}</Badge></span>
                    <span className="pc-table-cell--mono">{s.shares.toLocaleString()}</span>
                    <span style={{ color: s.benOwner ? '#059669' : '#94A3B8', fontWeight: 500, fontSize: '12.5px' }}>
                      {s.benOwner ? 'Yes' : 'No'}
                    </span>
                    <span>{s.fullyPaid ? <Badge variant="active">FP</Badge> : <Badge variant="ceased">Unpaid</Badge>}</span>
                    <span className="pc-table-cell--mono-sm">{s.email || '—'}</span>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ padding: '12px 0', color: '#94A3B8', fontSize: '13px' }}>
                No shareholders data available
              </div>
            )}
          </Collapse>

          {/* Document History */}
          <Collapse title="Document History" count={docs.length} icon={Icons.doc} defaultOpen={false}>
            {docs.length > 0 ? (
              <>
                <div className="pc-filters">
                  {formTypes.map(f => (
                    <button
                      key={f}
                      className={`pc-filter-btn ${docFilter === f ? 'pc-filter-btn--active' : ''}`}
                      onClick={() => setDocFilter(f)}
                    >
                      {f === 'all' ? 'All' : f}
                    </button>
                  ))}
                </div>
                <div className="pc-grid--docs pc-table-header">
                  <span>Form</span><span>Form Date</span><span>Lodge Date</span><span>ELS</span><span>ASIC Doc</span><span>Status</span>
                </div>
                {filteredDocs.map((d, i) => (
                  <div className="pc-grid--docs pc-table-row" key={i}>
                    <span><FormBadge formType={d.formType} /></span>
                    <span style={{ color: '#475569' }}>{d.formDate || '—'}</span>
                    <span style={{ color: '#475569' }}>{d.lodgeDate || '—'}</span>
                    <span className="pc-table-cell--dim">{d.els}</span>
                    <span className={d.asicDocNo ? 'pc-table-cell--mono' : 'pc-table-cell--dim'}>
                      {d.asicDocNo || '—'}
                    </span>
                    <span>
                      {d.status === 'VALIDATED' && <Badge variant="validated">Validated</Badge>}
                      {d.status === 'Prepared' && <Badge variant="prepared">Prepared</Badge>}
                      {!d.status && <span className="pc-table-cell--dim">—</span>}
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <div style={{ padding: '12px 0', color: '#94A3B8', fontSize: '13px' }}>
                No document history available
              </div>
            )}
          </Collapse>
        </div>
      </div>
    </div>
  );
}


const Details = (props) => {
  const dispatch = useDispatch();
  const companyProfile = useSelector((state) => state.companyProfile);
  const regdInformation = useSelector((state) => state.regdInformation);
  const companyHierarchy = useSelector((state) => state.companyHierarchy);
  const documentTimeline = useSelector((state) => state.documentTimeline);
  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const [selectedOfficerToggle, setSelectedOfficerToggle] = useState("Current");
  const [selectedCompanyViewToggle, setSelectedCompanyViewToggle] =
    useState("Detailed View");
  //company Profile Report
  // eslint-disable-next-line
  const [companyProfilesubmitted, setCompanyProfileSubmitted] = useState(false);
  const [isCompanyLoaded, setIsCompanyLoaded] = useState(false);
  const [printCompany, setPrintCompany] = useState({
    docLodge: "Yes",
    officeHolders: "Yes",
    asDate: moment().format('DD/MM/yyyy')
  });

  let handleChange = (e) => {
    const { name, value } = e.target;
    const newCompany = { ...printCompany, [name]: value };
    setPrintCompany((printCompany) => (newCompany));

    console.log('PrintCompanyProfile.Component.jsx - newCompany', newCompany);
  };

  console.log({companyProfile: companyProfile?.items?.company?.ACN});

  let printCompanyProfileHandleClick = (e) => {
    e.preventDefault();
    setCompanyProfileSubmitted(true);
    const selectedSearchValue = localStorage.getItem("inputSearchValue");

    const selectedCompanyCode = JSON.parse(localStorage.getItem("companyCode"));

    if(props.searchCompany && props.searchCompany[0]) {
      dispatch(
        userActions.getCompanyReport(
          printCompany,
          props.searchCompany[0].Code,
          setIsCompanyLoaded
        )
      );
    } else if (selectedCompanyCode !== null) {
      dispatch(
        userActions.getCompanyReport(
          printCompany,
          selectedCompanyCode,
          setIsCompanyLoaded
        )
      );
    } else if (selectedSearchValue !== null) {
      JSON.parse(selectedSearchValue).map((value) => dispatchAPIs(value));
    }
  };

  function dispatchAPIs(value) {
    dispatch(
      userActions.getCompanyReport(printCompany, value.Code, setIsCompanyLoaded)
    );
  }

  function handleOfficerChange(e) {
    console.log("eeeeeeeeeee",e);
    setSelectedOfficerToggle(e);
  }
  // Company View Toggled Switch
  function handleCompanyViewChange(e) {
    setSelectedCompanyViewToggle(e);
  }

  console.log({props});

  // Fetch data when view changes
  useEffect(() => {
    const acn = props.searchCompany?.[0]?.ACN;
    console.log('useEffect triggered - ACN:', acn, 'View:', selectedCompanyViewToggle);

    if ((selectedCompanyViewToggle === "Org Chart" || selectedCompanyViewToggle === "Cap Table") && acn) {
      console.log('Fetching hierarchy for ACN:', acn);
      dispatch(userActions.getCompanyHierarchy(acn));
    }
  }, [props.searchCompany, selectedCompanyViewToggle, dispatch]);

  // Fetch document timeline when Docs Timeline view is selected
  useEffect(() => {
    const coCode = props.searchCompany?.[0]?.Code;

    if (selectedCompanyViewToggle === "Docs Timeline" && coCode) {
      console.log('Fetching document timeline for coCode:', coCode);
      dispatch(userActions.getDocumentTimeline(coCode));
    }
  }, [props.searchCompany, selectedCompanyViewToggle, dispatch]);

  // quickcompany Profile Report
  
  //loading spinner
  const [isQuickCompanyLoaded, setIsQuickCompanyLoaded] = useState(false);
  // eslint-disable-next-line
  const [submitted, setSubmitted] = useState(false);
  const selectedSearchValue = localStorage.getItem("inputSearchValue");
  const selectedCompanyCode = JSON.parse(localStorage.getItem("companyCode"));
 
  useEffect(() => {
    if (props.searchCompany) {
     
        dispatch(userActions.getRegdInformation(props.searchCompany && props.searchCompany[0] && props.searchCompany[0].ACN));
    }
    // eslint-disable-next-line
  }, []);
  function handleClick(e) {
    setSubmitted(true);
    if(props.searchCompany && props.searchCompany[0]) {
      dispatch(
        userActions.getQuickCompanyProfileReport(
          props.searchCompany[0].Code,
          setIsQuickCompanyLoaded
        )
      );

    } else if (selectedCompanyCode !== null) {
      dispatch(
        userActions.getQuickCompanyProfileReport(
          selectedCompanyCode,
          setIsQuickCompanyLoaded
        )
      );
    } else if (selectedSearchValue !== null) {
      JSON.parse(selectedSearchValue).map((value) =>
        dispatch(
          userActions.getQuickCompanyProfileReport(
            value.Code,
            setIsQuickCompanyLoaded
          )
        )
      );
    }
  }

  const history = useHistory();
  // officer details popup
  const [popupSecretariesShow, setPopupSecretariesShow] = useState(0);
  const [selectedoOfficerName, setSelectedoOfficerName] = useState("");
  const secretariesDetailHandleClose = () => setPopupSecretariesShow(false);
  const secretariesDetailHandleShow = (e, index, officerCode, officerName) => {
    e.preventDefault();

    setPopupSecretariesShow(index);
    setSelectedoOfficerName(officerName);
    dispatch(userActions.getOfficerDetailsReport(officerCode));
  };

  let officerDetailHandleClick = (e) => {
    e.preventDefault();
    secretariesDetailHandleClose();
  };

  //shareholders Popup

  const [popupShareHolderShow, setPopupShareHolderShow] = useState(0);
  const [selectedShareholdersName, setSelectedShareholdersName] = useState("");
  const shareHolderDetailHandleClose = () => setPopupShareHolderShow(false);
  const shareHolderDetailHandleShow = (
    e,
    index,
    code,
    holderType,
    OfficerName
  ) => {
    e.preventDefault();

    setPopupShareHolderShow(index);
    setSelectedShareholdersName(OfficerName);
    dispatch(userActions.getShareHolderDetailsReport(code, holderType));
  };
  let shareHolderDetailHandleClick = (e) => {
    e.preventDefault();
    shareHolderDetailHandleClose();
  };

  //email Id
  const [officerPersonal, setOfficerPersonal] = useState({
    officerEmailAddress: "",
    OfficerDidNumber: "",
  });
  const [popupEmailShow, setPopupEmailShow] = useState(false);
  const handlePopupEmailClose = () => setPopupEmailShow(false);
const handlePopupEmailShow = (e, index) => {
  e.preventDefault();

  setPopupEmailShow(index);
};

let handleEmailPopupClick = (e, rn,detail) => {
  e.preventDefault();
 //if (officerPersonal.emailAddress) {
    dispatch(userActions.postIndividualEmailID(rn, officerPersonal.emailAddress,detail));
    setTimeout(() => {
      dispatch(userActions.getCompanyProfileByCode(props.searchCompany  && props.searchCompany[0].Code));
    }, 0);
    // props.history.push(props.history.location);
    handlePopupEmailClose();
  //}
};

 let handleOfficerPersonalDetailsChange = (e) => {
  e.preventDefault();
  setOfficerPersonal((officerPersonal) => ({
    ...officerPersonal,
    [e.target.name]: e.target.value,
  }));
};
//officers did 
const [popupOfficersDidErrorShow, setPopupOfficersDidErrorShow] = useState(false);
const [rn, setRn] = useState("");
const [popupOfficersDidShow, setPopupOfficersDidShow] = useState(false);
const handleOfficersDidPopupClose = () => setPopupOfficersDidShow(false);
const handleOfficersDidPopupShow = (e, index) => {
  e.preventDefault();
  setPopupOfficersDidShow(index);
};


let handleDidPopupClick = (e, rn) => {
  e.preventDefault();

  //if (person.didNumber) {
    dispatch(userActions.postIndividualEntitiesDid(rn, officerPersonal.didNumber));
    setRn(rn)
    setPopupOfficersDidErrorShow(officerPersonal.didNumber);
    setTimeout(() => {
      
      dispatch(userActions.getCompanyProfileByCode(props.searchCompany  && props.searchCompany[0].Code));
    }, 0);
  
    //props.history.push(props.history.location);
 
    handleOfficersDidPopupClose();
 // }
};

const didUpdate = useSelector((state) => state.didUpdate);

const handleErrorOfficersDidPopupClose = () => setPopupOfficersDidErrorShow(false);
const handleErrorOfficersDidPopupShow = (e, index) => {
  e.preventDefault();
  setPopupOfficersDidErrorShow(index);
};

// share Holder email Id
const [shareholderPersonal, setShareholderPersonal] = useState({
  shareholderEmailAddress: "",
 
});
const [popupShareholderEmailShow, setPopupShareholderEmailShow] = useState(false);
const handlePopupShareholderEmailClose = () => setPopupShareholderEmailShow(false);
const handlePopupShareholderEmailShow = (e, index) => {
e.preventDefault();
console.log("indexemail",index);
setPopupShareholderEmailShow(index);
};

let handleShareholderEmailPopupClick = (e, rn) => {
e.preventDefault();
//if (shareholderPersonal.emailAddress) {
  dispatch(userActions.postIndividualEmailID(rn, shareholderPersonal.emailAddress));
  setTimeout(() => {
    dispatch(userActions.getCompanyProfileByCode(props.searchCompany  && props.searchCompany[0].Code));
  }, 0);
  // props.history.push(props.history.location);
  handlePopupShareholderEmailClose();
//}
};

let handleShareholderPersonalDetailsChange = (e) => {
e.preventDefault();
setShareholderPersonal((shareholderPersonal) => ({
  ...shareholderPersonal,
  [e.target.name]: e.target.value,
}));
};
useEffect(() => {
  setPopupShareholderEmailShow(false);
  setPopupEmailShow(false);
  
  // eslint-disable-next-line
}, []);
  return (
    <div>
      {console.log("unitrustRecordaaaaa:::::::", props.searchCompany)}
      <div className="py-2"></div>
      <ThreeWayToggle
        name="companyViewToggle"
        selected={selectedCompanyViewToggle}
        onChange={(e) => handleCompanyViewChange(e)}
        options={VIEW_OPTIONS}
        toggleWidth={"750px"}
      />
    {selectedCompanyViewToggle === "Detailed View" ? (
      <DetailedViewSection companyProfile={companyProfile} entityLookup={props.entityLookup} onCompanyChange={props.onCompanyChange} />
    ) : selectedCompanyViewToggle === "Snapshot View" ? (
      <CompanySnapshotComponent
        history={props.history}
        setRecentSearch={props.setRecentSearch}
        recentSearch={props.recentSearch}
        setSearchCompany={props.setSearchCompany}
        searchCompany={props.searchCompany}
        loggedUserInfo={props.loggedUserInfo}
        userId={props.userId}
        clientId={props.clientId}
      />
    ) : selectedCompanyViewToggle === "Org Chart" ? (
      <div className="org-chart-container mt-3">
        <OrgStructureChart
          data={companyHierarchy?.items}
          loading={companyHierarchy?.loading}
          error={companyHierarchy?.error}
        />
      </div>
    ) : selectedCompanyViewToggle === "Docs Timeline" ? (
      <div className="org-chart-container mt-3">
        <DocumentTimeline
          data={documentTimeline?.items}
          loading={documentTimeline?.loading}
          error={documentTimeline?.error}
          documentUrl={props.documentUrl}
        />
      </div>
    ) : selectedCompanyViewToggle === "Cap Table" ? (
      <div className="org-chart-container mt-3">
        <CapTable
          data={companyHierarchy?.items}
          loading={companyHierarchy?.loading}
          error={companyHierarchy?.error}
        />
      </div>
    ) : null}
  
    </div>
  );
};
export default Details;
